import './styles/index.css';
import { initTheme } from './services/theme.js';
import { initRouter, registerRoute } from './router.js';
import { renderLogin } from './pages/login.js';
import { renderHome } from './pages/home.js';
import { renderUpload } from './pages/upload.js';
import { renderDebrief } from './pages/debrief.js';
import { renderProfile } from './pages/profile.js';
import { renderCompetitions } from './pages/competitions.js';
import { renderTeams } from './pages/teams.js';
import { renderQuiz } from './pages/quiz.js';
import { renderLeaderboards } from './pages/leaderboards.js';
import { renderShare } from './pages/share.js';
import { getCurrentUser } from './services/supabase.js';
import { initFirstRunTutorial } from './components/app-tutorial.js';
import { initLanguage, t } from './services/i18n.js';

registerRoute('#/login', renderLogin);
registerRoute('#/home', renderHome);
registerRoute('#/upload', renderUpload);
registerRoute('#/debrief/:id', renderDebrief);
registerRoute('#/profile', renderProfile);
registerRoute('#/competitions', renderCompetitions);
registerRoute('#/teams', renderTeams);
registerRoute('#/quiz', renderQuiz);
registerRoute('#/leaderboards', renderLeaderboards);
registerRoute('#/share/:id', renderShare);

// Safe localStorage wrapper
function lsGet(key, fallback = null) {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}
function lsSet(key, val) {
  try { localStorage.setItem(key, val); } catch {}
}

// PWA install prompt
let deferredPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;
  const visits = parseInt(lsGet('fd_visits', '0'), 10);
  if (visits >= 2 && !lsGet('fd_install_dismissed')) {
    showInstallBanner();
  }
});

const visits = parseInt(lsGet('fd_visits', '0'), 10) + 1;
lsSet('fd_visits', String(visits));

function showInstallBanner() {
  if (document.getElementById('install-banner')) return;
  const banner = document.createElement('div');
  banner.id = 'install-banner';
  banner.style.cssText = `
    position:fixed;top:0;left:50%;transform:translateX(-50%);max-width:480px;width:100%;
    background:#0f5132;padding:12px 16px;
    display:flex;align-items:center;justify-content:space-between;z-index:3000;
    border-radius:0 0 12px 12px;color:#fff;font-weight:500;font-size:14px;
  `;
  banner.innerHTML = `
    <span>${t('installApp')}</span>
    <div style="display:flex;gap:8px;">
      <button id="btn-install" style="background:#fff;color:#0f5132;border:none;border-radius:8px;padding:6px 12px;font-weight:600;cursor:pointer;">${t('install')}</button>
      <button id="btn-dismiss" style="background:rgba(255,255,255,0.2);color:#fff;border:none;border-radius:8px;padding:6px 12px;cursor:pointer;">✕</button>
    </div>
  `;
  document.body.appendChild(banner);

  banner.querySelector('#btn-install').addEventListener('click', async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem('fd_install_dismissed', '1');
      banner.remove();
    }
  });

  banner.querySelector('#btn-dismiss').addEventListener('click', () => {
    localStorage.setItem('fd_install_dismissed', '1');
    banner.remove();
  });
}

// Realtime toast for new debriefs
async function initRealtime() {
  try {
    const { supabase } = await import('./services/supabase.js');
    const user = await getCurrentUser();
    if (!user) return;

    supabase
      .channel('feed-items')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'debriefs', filter: `user_id=eq.${user.id}` }, (payload) => {
      showToast(t('newDebriefToast'));
      })
      .subscribe();
  } catch {}
}

function showToast(msg) {
  const t = document.createElement('div');
  t.className = 'toast show';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => { t.className = 'toast'; setTimeout(() => t.remove(), 300); }, 4000);
}

// Service worker: register, check for updates, and refresh once when a new SW takes control.
function initServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => registration.update())
      .catch(() => {});
  });
}

initLanguage();
initTheme();
initRouter();
initRealtime();
initFirstRunTutorial();
initServiceWorker();
